import React, {
  ReactNode,
  createContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { GraphConfiguration } from "@/type/common";
import { useGraphConfig } from "./GraphConfigContext";
import { deepClone } from "@/utils/refactor";

// Define the context type
type GraphConfigFormContextType = {
  config: GraphConfiguration | null;
  setConfig: (config: GraphConfiguration, noTimout?: boolean) => void;
  resetLocalConfig: () => void;
};
// Create the initial context value
const initialGraphConfigFormContext: GraphConfigFormContextType = {
  config: null,
  setConfig: () => null,
  resetLocalConfig: () => null,
};

// Create the context
const GraphConfigFormContext = createContext<GraphConfigFormContextType>(
  initialGraphConfigFormContext
);

// Create the provider component
type GraphConfigFormProviderProps = {
  children: ReactNode;
};

const safeConfig = (config: GraphConfiguration) => {
  config.data_source.domain = config.data_source.domain.filter(
    (d: any) => d[0] !== ""
  );

  config.graph_options.measures = config.graph_options.measures.filter(
    (m: any) => m.field !== ""
  );

  config.table_options.columns = config.table_options.columns.filter(
    (c: any) => c.field !== ""
  );

  config.data_source.groupBy = config.data_source.groupBy.filter(
    (g: any) => g.field !== ""
  );

  return config;
};

const getCleanConfig = (config: GraphConfiguration) => {
  const groupByFields = config.data_source.groupBy
    .filter((g: any) => g.field !== "")
    .map((g: any) => g.field);
  const columnsFields = config.table_options.columns
    .filter((c: any) => c.field !== "")
    .map((c: any) => c.field);
  const measuresFields = config.graph_options.measures
    .filter((m: any) => m.field !== "")
    .map((m: any) => m.field);

  if (
    config.data_source.orderBy.field &&
    !groupByFields.includes(config.data_source.orderBy.field) &&
    !columnsFields.includes(config.data_source.orderBy.field) &&
    !measuresFields.includes(config.data_source.orderBy.field)
  ) {
    config.data_source.orderBy.field = "";
  }

  if (config.block_options.only_count) {
    config.block_options.aggregation = "count";
    config.block_options.field = "id";
  }

  if (config.graph_options.only_count) {
    config.graph_options.measures = [
      {
        field: "id",
        aggregation: "count",
        yAxis: "left",
        displayName: "Number of records",
      },
    ];
  }

  if (
    ["pie", "donut"].includes(config.graph_options.chartType) &&
    config.graph_options.measures.length > 1
  ) {
    config.graph_options.measures = config.graph_options.measures.slice(0, 1);
  }

  return config;
};

export const GraphConfigFormProvider: React.FC<
  GraphConfigFormProviderProps
> = ({ children }) => {
  const { config: originalConfig, setConfig: setOriginalConfig } =
    useGraphConfig();
  const [config, setLocaleConfig] = useState<GraphConfiguration | null>(null);

  const timeout = useRef<NodeJS.Timeout | null>(null);

  const setConfig = (updatedConfig: GraphConfiguration, noTimout = false) => {
    const cleanConfig = getCleanConfig(updatedConfig);
    setLocaleConfig(deepClone(cleanConfig));
    if (!noTimout) {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        setOriginalConfig(safeConfig(cleanConfig));
      }, 500);
    } else {
      setOriginalConfig(safeConfig(cleanConfig));
    }
  };

  const resetLocalConfig = () => {
    setLocaleConfig(null);
  };

  const contextValue: GraphConfigFormContextType = {
    config,
    setConfig,
    resetLocalConfig,
  };

  useEffect(() => {
    if (originalConfig) {
      setLocaleConfig(originalConfig);
    }
  }, []);

  if (!config) {
    return null;
  }

  return (
    <GraphConfigFormContext.Provider value={contextValue}>
      {children}
    </GraphConfigFormContext.Provider>
  );
};

export default GraphConfigFormContext;
